// Free social actions — like, repost (branch), follow — signed by the BTC
// wallet and funded by Twetch's paymaster.
//
// An action is a sponsored post with different cells: the wallet signs the
// action's AIP domain, the request carries `{ sponsored, aip }` and NO
// transaction, and Twetch builds a zero-payout action on-chain. Nobody is
// paid and the actor pays nothing. Requests go through the same-origin bridge,
// the only place Twetch accepts BTC-signed sponsored writes from.
//
// `actionAipText` mirrors the server's `sponsor::action_aip_parts` cell for
// cell. Every cell is ASCII, so the wallet's string signer covers exactly the
// bytes the server verifies.

import { TWETCH_ALGORITHM, type TwetchIdentity } from './twetch-auth'
import type { DeviceKey } from './twetch-crypto'
import { twetchSignedPost } from './twetch-transport'
import { walletSignatureString } from './wallet-signature'

const MAP_PREFIX = '1PuQa7K62MiKCtssSLKy1kh56WWU7MtUR5'

export type TwetchAction = 'like' | 'branch' | 'follow'

/// OP_RETURN marker ("j" = 0x6a) + the MAP action cells + the replay timestamp.
export function actionAipText(
	action: TwetchAction,
	refKey: 'tx' | 'account',
	refVal: string,
	tsMs: number
): string {
	if (!Number.isSafeInteger(tsMs) || !refVal) {
		throw new Error('This action cannot be signed.')
	}
	return `j${[MAP_PREFIX, 'SET', 'app', 'twetch', 'type', action, refKey, refVal, 'ts', String(tsMs), '|'].join('')}`
}

interface ActionTarget {
	/// The API path that records the action.
	path: string
	refKey: 'tx' | 'account'
	/// What the server will sign against: a post's txid (its id when it has
	/// none), or the followed user's id.
	refVal: string
}

export function likeTarget(post: { id: number; txid?: string | null }): ActionTarget {
	return { path: `/v1/posts/${post.id}/like`, refKey: 'tx', refVal: post.txid || String(post.id) }
}

export function branchTarget(post: { id: number; txid?: string | null }): ActionTarget {
	return { path: `/v1/posts/${post.id}/branch`, refKey: 'tx', refVal: post.txid || String(post.id) }
}

export function followTarget(userId: number): ActionTarget {
	return { path: `/v1/users/${userId}/follow`, refKey: 'account', refVal: String(userId) }
}

/// One wallet popup, one free on-chain action.
export async function sendSponsoredAction<T>(opts: {
	action: TwetchAction
	target: ActionTarget
	identity: TwetchIdentity
	/// Null for a linked wallet, which cannot hold a session — the wallet
	/// signature below is the whole authorization.
	deviceKey: DeviceKey | null
	signWithWallet: (text: string) => Promise<string>
	now?: () => number
}): Promise<T> {
	const ts = (opts.now ?? Date.now)()
	const signature = walletSignatureString(
		await opts.signWithWallet(actionAipText(opts.action, opts.target.refKey, opts.target.refVal, ts))
	)
	return twetchSignedPost<T>(
		opts.target.path,
		{
			userId: opts.identity.uid,
			sponsored: true,
			aip: {
				algorithm: TWETCH_ALGORITHM,
				address: opts.identity.address,
				signature,
				ts
			}
		},
		opts.deviceKey ? { uid: opts.identity.uid, deviceKey: opts.deviceKey } : null
	)
}
